import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettings } from '../../contexts/SettingsContext';
import { THEMES, groupedThemes } from '../../themes';
import {
  AA_GRAPHIC,
  AA_TEXT,
  CHART_MIN_DELTA_E,
  MUTED_MIN,
  contrast,
  deltaE,
  parseColor,
} from '../../utils/contrast.mjs';
import ThemePreview from '../common/ThemePreview';
import './AdminThemes.css';

const SURFACES = ['--bg-primary', '--bg-card'];
const ACCENTS = [
  '--accent-orange', '--accent-blue', '--accent-gold',
  '--accent-green', '--accent-red',
];
const TEXTS = ['--text-primary', '--text-secondary'];

// Tokens som läses ur varje tema. Ordningen styr tabellen.
const PROBED = [...SURFACES, ...TEXTS, '--text-muted', ...ACCENTS, '--border-color'];

/**
 * Mäter ett temas tokens och returnerar alla kontrollresultat.
 *
 * Samma trösklar och samma matematik som `scripts/validate_themes.mjs` — de
 * delar `utils/contrast.js` just för att labbet inte ska kunna säga godkänt
 * medan CI säger nej.
 *
 * @param {object} theme - post ur temamanifestet.
 * @param {Record<string, string>} tokens - uppmätta, färdigresolvade värden.
 * @returns {Array<{label: string, value: number, min: number, unit: string, ok: boolean}>}
 */
function checkTheme(theme, tokens) {
  const rows = [];
  const surfaces = SURFACES
    .map((name) => ({ name, rgb: parseColor(tokens[name]) }))
    .filter((s) => s.rgb);

  const pair = (name, min) => {
    const rgb = parseColor(tokens[name]);
    if (!rgb) {
      rows.push({ label: name, value: 0, min, unit: ':1', ok: false });
      return;
    }
    for (const surface of surfaces) {
      const value = contrast(rgb, surface.rgb);
      rows.push({
        label: `${name.slice(2)} / ${surface.name.slice(5)}`,
        value, min, unit: ':1', ok: value >= min,
      });
    }
  };

  TEXTS.forEach((n) => pair(n, AA_TEXT));
  pair('--text-muted', MUTED_MIN);
  ACCENTS.forEach((n) => pair(n, AA_TEXT));

  // Diagrampaletten mäts mot kortytan — det är där diagrammen ritas.
  const card = parseColor(tokens['--bg-card']);
  const charts = (theme.charts || []).map((c) => ({ hex: c, rgb: parseColor(c) }));

  charts.forEach((c) => {
    if (!c.rgb || !card) return;
    const value = contrast(c.rgb, card);
    rows.push({
      label: `diagram ${c.hex} / card`,
      value, min: AA_GRAPHIC, unit: ':1', ok: value >= AA_GRAPHIC,
    });
  });

  // Serier som inte går att skilja åt är lika oanvändbara som osynliga, och
  // kontrastkvot mäter fel sak här — därför ΔE.
  for (let i = 0; i < charts.length; i += 1) {
    for (let j = i + 1; j < charts.length; j += 1) {
      if (!charts[i].rgb || !charts[j].rgb) continue;
      const value = deltaE(charts[i].rgb, charts[j].rgb);
      rows.push({
        label: `ΔE ${charts[i].hex} ↔ ${charts[j].hex}`,
        value, min: CHART_MIN_DELTA_E, unit: '', ok: value >= CHART_MIN_DELTA_E,
      });
    }
  }

  return rows;
}

/**
 * Temalabbet — granska och mät sajtens teman.
 *
 * Två saker som validatorn i CI inte kan ge:
 *
 * 1. **Granska på sajten.** Ett tema påtvingas den egna sessionen så man kan
 *    gå runt på riktiga sidor i det. Miniatyrer visar paletten, men det är på
 *    matchsidan och i veto man ser vad som faktiskt skaver. Läget bor bara i
 *    minnet och sparas varken lokalt eller på kontot — en omladdning avslutar
 *    det, och en banderoll påminner om att det är på.
 *
 * 2. **Mätning av det renderade värdet.** CI-validatorn läser CSS-texten och
 *    ser bara de tokens temat deklarerar. Webbläsaren känner det slutgiltiga
 *    värdet inklusive arv — och arvet är precis där temabuggar gömmer sig
 *    (se ThemePreview om varför nollställningslagret finns). Siffrorna här är
 *    alltså sannare än textparsningens, med samma trösklar.
 *
 * @param {object} props
 * @param {object} props.perms - behörighetsobjektet från usePermissions.
 * @returns {JSX.Element}
 */
export default function AdminThemes({ perms }) {
  const { t } = useTranslation();
  const { previewThemeId, setPreviewThemeId } = useSettings();
  const [measured, setMeasured] = useState({});
  const [open, setOpen] = useState(null);
  const probeRefs = useRef({});

  // Mät en gång efter mount: sonderna är statiska och tokens ändras inte
  // förrän någon bygger om frontend.
  useEffect(() => {
    const out = {};
    for (const theme of THEMES) {
      const node = probeRefs.current[theme.id];
      if (!node) continue;
      const cs = getComputedStyle(node);
      out[theme.id] = Object.fromEntries(
        PROBED.map((name) => [name, cs.getPropertyValue(name).trim()]),
      );
    }
    setMeasured(out);
  }, []);

  const results = useMemo(() => {
    const out = {};
    for (const theme of THEMES) {
      if (!measured[theme.id]) continue;
      out[theme.id] = checkTheme(theme, measured[theme.id]);
    }
    return out;
  }, [measured]);

  const canManage = perms?.isOwner || perms?.hasAnyCommand?.('shop.manage');
  if (!canManage) {
    return <div className="admin-themes-denied">{t('admin.noAccess')}</div>;
  }

  return (
    <div className="admin-themes">
      <h1 className="admin-themes-title">{t('admin.themeLab.title')}</h1>
      <p className="admin-themes-intro">{t('admin.themeLab.intro')}</p>

      {/* Osynliga sonder. Nollställningslagret är nödvändigt av samma skäl som
          i ThemePreview: utan det mäter vi adminsidans tema för varje token
          som temat självt inte deklarerar. */}
      <div className="admin-themes-probes" aria-hidden="true">
        {THEMES.map((theme) => (
          <div data-theme="dark" key={theme.id}>
            <div
              data-theme={theme.id}
              ref={(el) => { probeRefs.current[theme.id] = el; }}
            />
          </div>
        ))}
      </div>

      {groupedThemes().map(({ group, themes }) => (
        <section className="admin-themes-group" key={group}>
          <h2 className="admin-themes-group-title">
            {t(`settings.themeGroup.${group}`)}
          </h2>

          <div className="admin-themes-grid">
            {themes.map((theme) => {
              const rows = results[theme.id] || [];
              const failed = rows.filter((r) => !r.ok);
              const isOpen = open === theme.id;
              const isPreviewing = previewThemeId === theme.id;

              return (
                <div className="admin-theme-card" key={theme.id}>
                  <div className="admin-theme-card-preview">
                    <ThemePreview themeId={theme.id} label={theme.label} />
                  </div>

                  <div className="admin-theme-card-head">
                    <span className="admin-theme-card-name">{theme.label}</span>
                    <code className="admin-theme-card-id">{theme.id}</code>
                  </div>

                  <div className="admin-theme-card-meta">
                    <span className="admin-theme-tag">{theme.polarity}</span>
                    {rows.length === 0 ? (
                      <span className="admin-theme-tag">…</span>
                    ) : (
                      <span
                        className={failed.length === 0
                          ? 'admin-theme-tag admin-theme-tag--ok'
                          : 'admin-theme-tag admin-theme-tag--fail'}
                      >
                        {failed.length === 0
                          ? t('admin.themeLab.pass', { count: rows.length })
                          : t('admin.themeLab.fail', { count: failed.length })}
                      </span>
                    )}
                  </div>

                  <div className="admin-theme-card-actions">
                    <button
                      type="button"
                      className={isPreviewing
                        ? 'admin-theme-btn admin-theme-btn--active'
                        : 'admin-theme-btn'}
                      onClick={() => setPreviewThemeId(isPreviewing ? null : theme.id)}
                    >
                      {isPreviewing
                        ? t('admin.themeLab.previewExit')
                        : t('admin.themeLab.previewStart')}
                    </button>
                    <button
                      type="button"
                      className="admin-theme-btn admin-theme-btn--ghost"
                      onClick={() => setOpen(isOpen ? null : theme.id)}
                    >
                      {t('admin.themeLab.measurements')}
                    </button>
                  </div>

                  {isOpen && (
                    <table className="admin-theme-table">
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.label}
                            className={r.ok ? '' : 'admin-theme-row--fail'}
                          >
                            <td>{r.label}</td>
                            <td className="admin-theme-num">
                              {r.value.toFixed(2)}{r.unit}
                            </td>
                            <td className="admin-theme-min">
                              ≥ {r.min}{r.unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
